/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import TranslateIcon from "@mui/icons-material/Translate";
// react-router components
import { useLocation, Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";

import { useAuth } from "context/authContext";
import i18n from "i18next";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
  navbarFilterControl,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
  setDirection,
} from "context";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MDTypography from "components/MDTypography";
import { ArrowDropDown } from "@mui/icons-material";
import { getNotifiations } from "services/api";
import { readAllNotifications } from "services/api";
import NotificationMenu from "./components/NotificationItem";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";
import NotificationsMenu from "components/NotificationsMenu";
import UserMenu from "components/UserMenu";
import TicketsMenu from "components/TicketsMenu";
import TicketModal from "components/TicketModal";

function DashboardNavbar({ absolute, light, isMini }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    switchView,
    employee,
    viewAsAdmin,
    selectedMonth,
    selectedYear,
    setSelectedMonth,
    setSelectedYear,
    checkLoggedInStatus,
    tickets,
    setTickets,
    quarter,
    setQuarter,
    isQuarterly,
    setIsQuarterly,
  } = useAuth();
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode, direction } =
    controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [dateAnchor, setDateAnchor] = useState(null);
  const openDateMenu = (event) => setDateAnchor(event.currentTarget);
  const closeDateMenu = () => setDateAnchor(null);
  // A helper to get the month name from the number
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const months = t("navigationBar.months", { returnObjects: true });
  const smallScreenMonth = t("navigationBar.smallScreenMonth", { returnObjects: true });

  // const smallScreenMonth = [
  //   "Jan",
  //   "Feb",
  //   "Mar",
  //   "Apr",
  //   "May",
  //   "Jun",
  //   "Jul",
  //   "Aug",
  //   "Sept",
  //   "Oct",
  //   "Nov",
  //   "Dec",
  // ];
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const { chats } = useAuth();
  const [notificationsAnchor, setNotificationsAnchor] = useState(null);
  const [ticketMenuAnchor, setTicketMenuAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]); // your notifications list
  const unreadCount = notifications?.filter((n) => !n.read).length;

  // Modal State
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const handleModalOpen = () => setTicketModalOpen(true);
  const handleModalClose = () => setTicketModalOpen(false);

  const { companies } = useAuth();
  const handleNotificationsOpen = (event) => {
    setNotificationsAnchor(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };

  useEffect(() => {
    // replace with your service call
    getNotifiations(employee.id).then((not) => {
      if (not !== "error") {
        setNotifications(not);
      }
    });
  }, [employee.id]);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  const handleTicketMenuOpen = (event) => setTicketMenuAnchor(event.currentTarget);
  const handleTicketMenuClose = () => setTicketMenuAnchor(null);

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  return (
    <>
      <AppBar
        position={absolute ? "absolute" : navbarType}
        color="inherit"
        sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
      >
        <Toolbar
          sx={(theme) => ({
            ...navbarContainer(theme),
            position: "relative",
          })}
        >
          <IconButton
            onClick={handleMiniSidenav}
            disableRipple
            sx={(theme) => ({
              position: "absolute",
              left: 2, // or right: before user profile side if RTL
              top: "50%",
              transform: "translateY(-50%)",

              width: 20,
              height: 20,
              minWidth: 20,
              minHeight: 20,
              padding: 0,

              borderRadius: "8px",
              color: theme.palette.navBar.contrastText,

              backgroundColor: theme.functions.rgba(theme.palette.navBar.contrastText, 0.04),

              "&:hover": {
                backgroundColor: theme.functions.rgba(theme.palette.navBar.contrastText, 0.08),
              },
            })}
          >
            <Icon fontSize="small">{miniSidenav ? "menu" : "menu_open"}</Icon>
          </IconButton>
          {/* ===== LEFT SECTION: USER PROFILE ===== */}
          <MDBox
            display="flex"
            alignItems="center"
            onClick={handleUserMenuOpen}
            sx={{
              cursor: "pointer",
              gap: 0.5,
              mx: 1,
              px: 0.5,
              py: 0.5,

              borderRadius: "12px",

              border: `1px solid ${theme.functions.rgba(theme.palette.navBar.contrastText, 0.12)}`,

              backgroundColor: theme.functions.rgba(theme.palette.navBar.contrastText, 0.04),

              transition: "all 0.2s ease",

              "&:hover": {
                backgroundColor: theme.functions.rgba(theme.palette.navBar.contrastText, 0.08),

                borderColor: theme.functions.rgba(theme.palette.navBar.contrastText, 0.22),
              },
            }}
          >
            <MDBox position="relative">
              <Avatar
                src={`data:image/jpeg;base64,${employee.photo}`}
                sx={{
                  width: 38,
                  height: 38,

                  border: `2px solid ${theme.functions.rgba(
                    theme.palette.navBar.contrastText,
                    0.15
                  )}`,

                  boxShadow: theme.shadows[2],
                }}
              />

              <MDBox
                sx={{
                  position: "absolute",
                  bottom: 2,
                  right: 2,

                  width: 10,
                  height: 10,

                  borderRadius: "50%",
                  backgroundColor: "#4caf50",

                  border: `2px solid ${
                    theme.palette.navBar.main !== "transparent"
                      ? theme.palette.navBar.main
                      : theme.palette.background.default
                  }`,
                }}
              />
            </MDBox>

            {!isMobile && (
              <MDBox
                display="flex"
                flexDirection="column"
                justifyContent="center"
                lineHeight={1.2}
                sx={{
                  minWidth: 0,
                }}
              >
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  sx={{
                    color: theme.palette.navBar.contrastText,
                    fontSize: "0.9rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "180px",
                  }}
                >
                  {employee?.name}
                </MDTypography>

                <MDTypography
                  variant="caption"
                  sx={{
                    color: theme.palette.navBar.contrastText,
                    opacity: 0.65,
                    fontSize: "0.72rem",
                    letterSpacing: "0.03em",
                  }}
                >
                  {employee.role === 1 ? "CEO" : employee.isAdmin ? "Administrator" : "Employee"}
                </MDTypography>
              </MDBox>
            )}

            <ArrowDropDown
              sx={{
                color: theme.palette.navBar.contrastText,
                opacity: 0.7,
                fontSize: 22,
              }}
            />
          </MDBox>

          {/* ===== RIGHT SECTION: ACTION CENTER ===== */}
          <MDBox display="flex" alignItems="center" gap={1}>
            {!viewAsAdmin && (
              <>
                {/* ============================================= */}
                {/* 🖥️ DESKTOP VIEW (md and up)                   */}
                {/* ============================================= */}
                <MDBox display={{ xs: "none", md: "flex" }} alignItems="center" gap={1}>
                  {/* 1. Mode Toggler (Monthly <-> Quarterly) */}
                  <MDBox
                    onClick={() => setIsQuarterly(!isQuarterly)}
                    sx={{
                      ...navbarFilterControl(theme),
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 1,
                      opacity: 0.8,
                      transition: "opacity 0.2s",
                      "&:hover": { opacity: 1 },
                    }}
                  >
                    <Icon
                      sx={{
                        color: `${theme.palette.navBar.contrastText} !important`,
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        mr: 0.5,
                      }}
                    >
                      sync_alt
                    </Icon>
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      sx={{
                        color: `${theme.palette.navBar.contrastText} !important`,
                        fontSize: "0.85rem",
                        fontWeight: 600,
                      }}
                    >
                      {isQuarterly ? t("navigationBar.quarterly") : t("navigationBar.monthly")}
                    </MDTypography>
                  </MDBox>

                  {/* 2. Month OR Quarter Select */}
                  <FormControl variant="standard">
                    <Select
                      value={isQuarterly ? quarter : selectedMonth}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        if (isQuarterly) {
                          setQuarter(val);
                          // Optional: If you need to sync the month state immediately:
                          // setSelectedMonth(val * 3);
                        } else {
                          setSelectedMonth(val);
                        }
                      }}
                      disableUnderline
                      sx={(theme) => ({
                        ...navbarFilterControl(theme),
                        minWidth: 90,

                        "& .MuiSelect-select": {
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          textAlign: "center",
                          paddingRight: "24px !important",
                          paddingLeft: "24px !important",
                        },

                        "& .MuiSelect-icon": {
                          right: 8,
                        },
                        color: `${theme.palette.navBar.contrastText} !important`,
                      })}
                    >
                      {isQuarterly
                        ? [
                            { val: 1, label: t("navigationBar.q1") },
                            { val: 2, label: t("navigationBar.q2") },
                            { val: 3, label: t("navigationBar.q3") },
                            { val: 4, label: t("navigationBar.q4") },
                          ].map((q) => (
                            <MenuItem key={q.val} value={q.val}>
                              {q.label}
                            </MenuItem>
                          ))
                        : // : monthNames.map((m, i) => (
                          //     <MenuItem key={m} value={i + 1}>
                          //       {m}
                          //     </MenuItem>
                          //   ))
                          months.map((m, i) => (
                            <MenuItem key={m} value={i + 1}>
                              {m}
                            </MenuItem>
                          ))}
                    </Select>
                  </FormControl>

                  {/* 3. Year Select (Shared) */}
                  <FormControl variant="standard">
                    <Select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(Number(e.target.value))}
                      disableUnderline
                      sx={(theme) => ({
                        ...navbarFilterControl(theme),
                        minWidth: 90,

                        "& .MuiSelect-select": {
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          textAlign: "center",
                          paddingRight: "24px !important",
                          paddingLeft: "24px !important",
                        },

                        "& .MuiSelect-icon": {
                          right: 8,
                        },
                        color: `${theme.palette.navBar.contrastText} !important`,
                      })}
                    >
                      {/* {[2024, 2025, 2026].map((y) => (
                        <MenuItem key={y} value={y}>
                          {y}
                        </MenuItem>
                      ))} */}
                      {[2024, 2025, 2026].map((y) => (
                        <MenuItem key={y} value={y}>
                          {i18n.language === "ar"
                            ? new Intl.NumberFormat("ar-EG", { useGrouping: false }).format(y)
                            : y}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MDBox>

                {/* ============================================= */}
                {/* 📱 MOBILE VIEW (xs and sm)                    */}
                {/* ============================================= */}
                <MDBox display={{ xs: "flex", md: "none" }} alignItems="center">
                  {/* Mobile Trigger Button */}
                  <MDBox
                    onClick={openDateMenu}
                    sx={(theme) => ({
                      ...navbarFilterControl(theme),
                      minWidth: 90,

                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                      paddingRight: "24px !important",
                      paddingLeft: "24px !important",
                      gap: 0.5,
                      "& .MuiSelect-icon": {
                        right: 8,
                      },
                      border: `1px solid ${theme.palette.navBar.contrastText}22`,
                    })}
                  >
                    <Icon
                      sx={{ color: theme.palette.navBar.contrastText, fontSize: "18px !important" }}
                    >
                      {isQuarterly ? "pie_chart" : "calendar_today"}
                    </Icon>
                    <MDTypography
                      variant="button"
                      fontWeight="bold"
                      sx={{ color: theme.palette.navBar.contrastText, fontSize: "0.75rem" }}
                    >
                      {isQuarterly
                        ? `${i18n.language === "ar" ? "ر" : "Q"}${
                            i18n.language === "ar"
                              ? new Intl.NumberFormat("ar-EG", { useGrouping: false }).format(
                                  quarter
                                )
                              : quarter
                          } - ${
                            i18n.language === "ar"
                              ? new Intl.NumberFormat("ar-EG", { useGrouping: false }).format(
                                  selectedYear
                                )
                              : selectedYear
                          }`
                        : `${smallScreenMonth[selectedMonth - 1]} ${
                            i18n.language === "ar"
                              ? new Intl.NumberFormat("ar-EG", { useGrouping: false }).format(
                                  selectedYear
                                )
                              : selectedYear
                          }`}
                    </MDTypography>
                  </MDBox>

                  {/* Mobile Popup Menu */}
                  <Menu
                    anchorEl={dateAnchor}
                    open={Boolean(dateAnchor)}
                    onClose={closeDateMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                    PaperProps={{
                      sx: { mt: 1.5, borderRadius: "12px", p: 2, minWidth: "220px" },
                    }}
                  >
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <MDTypography variant="caption" fontWeight="bold" color="text">
                        {t("navigationBar.period")}
                      </MDTypography>
                      {/* Mobile Toggle */}
                      <MDButton
                        size="small"
                        variant="text"
                        color="info"
                        onClick={() => setIsQuarterly(!isQuarterly)}
                        sx={{ fontSize: "0.7rem", minWidth: "auto", p: 0.5 }}
                      >
                        {t("navigationBar.switch")}{" "}
                        {isQuarterly ? t("navigationBar.monthly") : t("navigationBar.quarterly")}
                      </MDButton>
                    </MDBox>

                    <Divider sx={{ my: 1 }} />

                    <MDBox display="flex" flexDirection="column" gap={2}>
                      {/* Mobile Selector */}
                      <Select
                        fullWidth
                        value={isQuarterly ? quarter : selectedMonth}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          if (isQuarterly) setQuarter(val);
                          else setSelectedMonth(val);
                        }}
                        sx={{ height: "40px" }}
                      >
                        {isQuarterly
                          ? [
                              { val: 1, label: t("navigationBar.q1") },
                              { val: 2, label: t("navigationBar.q2") },
                              { val: 3, label: t("navigationBar.q3") },
                              { val: 4, label: t("navigationBar.q4") },
                            ].map((q) => (
                              <MenuItem key={q.val} value={q.val}>
                                {q.label}
                              </MenuItem>
                            ))
                          : // : monthNames.map((m, i) => (
                            //     <MenuItem key={m} value={i + 1}>
                            //       {m}
                            //     </MenuItem>
                            //   ))
                            months.map((m, i) => (
                              <MenuItem key={m} value={i + 1}>
                                {m}
                              </MenuItem>
                            ))}
                      </Select>

                      {/* Mobile Year Selector */}
                      <Select
                        fullWidth
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                        sx={{ height: "40px" }}
                      >
                        {[2024, 2025, 2026].map((y) => (
                          <MenuItem key={y} value={y}>
                            {i18n.language === "ar"
                              ? new Intl.NumberFormat("ar-EG", { useGrouping: false }).format(y)
                              : y}
                          </MenuItem>
                        ))}
                      </Select>
                    </MDBox>
                  </Menu>
                </MDBox>
              </>
            )}

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                mx: 1,
                height: "24px",
                my: "auto",
                opacity: 0.2,
                display: { xs: "none", md: "inline-flex" },
              }}
            />

            {/* Existing Notification & Settings Icons */}
            <IconButton
              color="inherit"
              onClick={handleNotificationsOpen}
              sx={(theme) => navbarIconButton(theme, theme)}
            >
              <Badge badgeContent={unreadCount} color="error" variant="dot">
                <NotificationsIcon sx={{ fontSize: "22px" }} />
              </Badge>
            </IconButton>

            {employee.role === 1 && (
              <IconButton
                onClick={handleConfiguratorOpen}
                color="inherit"
                sx={(theme) => ({
                  ...navbarIconButton(theme, theme),
                  // 🛠️ Responsive Logic:
                  // 'none' on extra-small/small (mobile)
                  // 'inline-flex' on medium and up (tablet/desktop)
                  display: { xs: "none", md: "inline-flex" },
                })}
              >
                <Icon sx={{ fontSize: "22px" }}>settings</Icon>
              </IconButton>
            )}
            {(employee.role === 1 || employee.isAdmin) && (
              <IconButton
                onClick={handleTicketMenuOpen}
                color="inherit"
                sx={(theme) => ({
                  ...navbarIconButton(theme, theme),
                  // 🛠️ Responsive Logic:
                  // 'none' on extra-small/small (mobile)
                  // 'inline-flex' on medium and up (tablet/desktop)
                  display: { xs: "none", md: "inline-flex" },
                })}
              >
                <Icon sx={{ fontSize: "22px" }}>contact_support</Icon>
              </IconButton>
            )}
          </MDBox>
        </Toolbar>
      </AppBar>

      {/* ===== USER MENU ===== */}
      <UserMenu
        anchorEl={userMenuAnchor}
        onClose={handleUserMenuClose}
        employee={employee}
        viewAsAdmin={viewAsAdmin}
        switchView={switchView}
        checkLoggedInStatus={checkLoggedInStatus} // Pass your auth check function here!
      />

      {/* ===== NOTIFICATIONS MENU (REFINED) ===== */}
      <NotificationsMenu
        anchorEl={notificationsAnchor}
        open={Boolean(notificationsAnchor)}
        onClose={handleNotificationsClose}
        notifications={notifications}
        setNotifications={setNotifications}
        employeeId={employee?.id} // Ensure employee ID is passed
        darkMode={darkMode}
      />

      {/* TICKETS DROPDOWN MENU */}
      {(employee.role === 1 || employee.isAdmin) && (
        <>
          <TicketsMenu
            anchorEl={ticketMenuAnchor}
            open={Boolean(ticketMenuAnchor)}
            onClose={handleTicketMenuClose}
            tickets={tickets || []}
            onOpenModal={handleModalOpen}
            darkMode={darkMode}
          />
          <TicketModal
            open={ticketModalOpen}
            onClose={handleModalClose}
            currentUser={employee}
            setTickets={setTickets} // Pass setter to update context instantly
          />{" "}
        </>
      )}
    </>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
